import {AfterViewInit, Component, Inject} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogTitle
} from '@angular/material/dialog';
import {PeoClientApiModel} from '../../../../model/peo-client-api.model';
import {PeoClientService} from '../../../../service/peo-client.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NgIf} from '@angular/common';
import {MatList, MatListItem, MatListItemIcon, MatListItemTitle} from '@angular/material/list';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatButton} from '@angular/material/button';
import {MatDivider} from "@angular/material/divider";

/**
 * @class
 * @description This class is responsible for rendering the PEO Client Document Split Status Dialog component.
 * @export
 * @name PeoClientDocumentSplitStatusAdminDialogComponent
 */
@Component({
  imports: [
    MatDialogContent,
    MatDialogTitle,
    NgIf,
    MatDialogActions,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatIcon,
    MatListItemTitle,
    MatProgressSpinner,
    MatDialogClose,
    MatButton,
    MatDivider
  ],
  selector: 'app-dialog-admin-peo-client-document-split-status',
  standalone: true,
  styleUrl: './peo-client-document-split-status.component.scss',
  templateUrl: './peo-client-document-split-status.component.html'
})
export class PeoClientDocumentSplitStatusAdminDialogComponent implements AfterViewInit {

  /**
   * @description This property contains the PEO Client data for the dialog.
   * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
   * @name PeoClientDocumentSplitStatusAdminDialogComponent#client
   * @property
   * @protected
   * @type {PeoClientApiModel?}
   */
  protected client?: PeoClientApiModel;

  /**
   * @default false
   * @description This property contains the loading state of the PEO Client's document split status.
   * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
   * @name PeoClientDocumentSplitStatusAdminDialogComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @constructor
   * @description This method initializes the PEO Client Document Split Status Dialog component.
   * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
   * @name PeoClientDocumentSplitStatusAdminDialogComponent#constructor()
   * @param {PeoClientService} service The injected PEO Client service provider.
   * @param {MatSnackBar} snackbar The injected Material Snackbar Reference provider.
   * @param {{ client: PeoClientApiModel }} data The data for the dialog.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected PEO Client service provider.
     * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
     * @name PeoClientDocumentSplitStatusAdminDialogComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoClientService}
     */
    private readonly service: PeoClientService,
    /**
     * @description The injected Material Snackbar Reference provider.
     * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
     * @name PeoClientDocumentSplitStatusAdminDialogComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar,
    /**
     * @description The data for the dialog.
     * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
     * @name PeoClientDocumentSplitStatusAdminDialogComponent#data
     * @property
     * @public
     * @type {{ client: PeoClientApiModel, taxYear?: number }}
     */
    @Inject(MAT_DIALOG_DATA)
    public data: { client: PeoClientApiModel, taxYear?: number }
  ) {
  }

  /**
   * @description This method loads the document split status for the client.
   * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
   * @method
   * @name PeoClientDocumentSplitStatusAdminDialogComponent#loadDocumentSplitStatus()
   * @protected
   * @returns {void}
   */
  protected loadDocumentSplitStatus(): void {

    // Reset the loading state into the instance.
    this.loading = true;

    // Load the document split status for the client.
    this.service.loadDocumentSplitStatus(this.data.client.peoId, this.data.client.clientId, this.data.taxYear)
      .subscribe({

        // Handle the error response from the server.
        error: ((e: any): void => {

          // Reset the loading state into the instance.
          this.loading = false;

          // Send the snackbar notification to the user.
          this.snackbar.open(`Loading Document Split Status for Client failed with:  ${e.message}`, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom',
          });
        }),

        // Handle the successful response from the server.
        next: ((c: PeoClientApiModel): void => {

          // Update the client data.
          this.client = c;

          // Reset the loading state into the instance.
          this.loading = false;
        })
      });
  }

  /**
   * @description This method is called after the view has been initialized.
   * @memberof PeoClientDocumentSplitStatusAdminDialogComponent
   * @method
   * @name PeoClientDocumentSplitStatusAdminDialogComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Load the document split status for the client.
    this.loadDocumentSplitStatus();
  }
}
